import { CaretLeft } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { KuniriLogo } from '@/components/kuniri-logo'

interface AuthLayoutProps {
  title: string
  subtitle?: ReactNode
  maxWidth?: 520 | 560 | 640
  backTo?: string
  /** Login keeps the brand subtitle as the visual headline and exposes the
   * route title to assistive technology only. */
  titleVisible?: boolean
  /** Screens that need the heading below an icon render their own h1. */
  titleInChildren?: boolean
  children: ReactNode
}

const WIDTH_CLASS: Record<NonNullable<AuthLayoutProps['maxWidth']>, string> = {
  520: 'max-w-[520px]',
  560: 'max-w-[560px]',
  640: 'max-w-[640px]',
}

/**
 * Shared auth chrome: back control, logo, title and the frozen max widths from
 * UI_PARITY §5 (login 520, register 560). The mobile title is 25px — the
 * Flutter screen uses a real heading rather than the tiny fallback the first
 * React pass rendered.
 */
export function AuthLayout({
  title,
  subtitle,
  maxWidth = 520,
  backTo = '/onboarding',
  titleVisible = true,
  titleInChildren = false,
  children,
}: AuthLayoutProps) {
  const { t } = useTranslation()

  return (
    <main id="main-content" className="min-h-dvh bg-paper">
      <div
        className={`mx-auto flex w-full flex-col px-6 pt-[calc(var(--safe-top)+12px)] pb-8 md:py-10 ${WIDTH_CLASS[maxWidth]}`}
      >
        <header className="flex items-center">
          <Link
            to={backTo}
            className="btn-ghost size-11 justify-center rounded-full p-0"
            aria-label={t('common.back')}
          >
            <CaretLeft size={26} weight="bold" aria-hidden="true" />
          </Link>
        </header>

        <div className="mt-2 flex justify-center">
          <KuniriLogo height={54} />
        </div>

        {titleInChildren ? null : (
          <div className="mt-4 space-y-2 text-center">
            <h1
              className={
                titleVisible
                  ? 'text-[25px] leading-none font-extrabold text-deep'
                  : 'sr-only'
              }
            >
              {title}
            </h1>
            {subtitle ? (
              <p className="whitespace-pre-line text-[15px] leading-snug text-muted">
                {subtitle}
              </p>
            ) : null}
          </div>
        )}

        <div className="mt-6">{children}</div>
      </div>
    </main>
  )
}
